import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Icon, Text } from '@Sage/hoi-poi-ui';
import { PRODUCTS } from 'constants/Entities';
import Context from 'managers/Context';
import { ProductRatesModel } from 'models';
import { getLiteral } from 'utils/getLiteral';
import { getSrcProductCircleAvatar } from 'utils/getSrcAvatar';

import { RatesAddItemsEmpty } from '../shared/RatesAddItemsFeedback';
import RatesAddItemRow from '../shared/RatesAddItemRow';
import RatesAddItemsModal from '../shared/RatesAddItemsModal';

const ProductSection = memo(({ children, onFolderClick, section }) => {
    const pathParts = section.path ? section.path.split('/') : [];
    const pathIds = section.pathid ? section.pathid.split('/') : ['-1'];
    pathParts.splice(0, 1, 'Products');

    return (
        <div className="rates-add-items-modal__section">
            <div className="rates-add-items-modal__breadcrumb">
                {pathParts.map((part, index) => {
                    const isLast = index + 1 === pathParts.length;
                    const folderId = pathIds[index];
                    return (
                        <span className="rates-add-items-modal__breadcrumb-item" key={index}>
                            {index > 0 ? <Icon name="chevronRight" size="small" /> : null}
                            <Text
                                onClick={isLast ? undefined : () => onFolderClick(Number(folderId))}
                            >
                                {part.toUpperCase()}
                            </Text>
                        </span>
                    );
                })}
            </div>
            <div className="rates-add-items-modal__list">{children}</div>
        </div>
    );
});

ProductSection.displayName = 'ProductSection';

const ProductRow = memo(({ isAdded, onAdd, product }) => {
    const { fallbackSrc, src } = getSrcProductCircleAvatar(product.thumbnail);

    return (
        <RatesAddItemRow
            isAdded={isAdded}
            label={product.name}
            leading={
                <img
                    alt=""
                    src={src}
                    onError={(event) => {
                        event.currentTarget.src = fallbackSrc;
                    }}
                />
            }
            onAction={onAdd}
        />
    );
});

ProductRow.displayName = 'ProductRow';

const ProductRatesAddModal = memo(({ handleClose, onAddLine, open, productsList, title }) => {
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [result, setResult] = useState([]);

    const loadProducts = useCallback((text, idFolder = -1) => {
        setLoading(true);
        setError(false);
        Context.entityManager.getEntitiesManager(PRODUCTS).getSalesOrderProducts(
            idFolder,
            text,
            '',
            '-1',
            (response) => {
                setResult(response || []);
                setLoading(false);
                setError(false);
            },
            () => {
                setResult([]);
                setError(true);
                setLoading(false);
            },
        );
    }, []);

    useEffect(() => {
        if (open) {
            setSearch('');
            loadProducts('', -1);
        }
    }, [loadProducts, open]);

    const selectedIds = useMemo(() => {
        const ids = new Set();
        productsList?.forEach((item) => ids.add(String(item.id)));
        return ids;
    }, [productsList]);

    const handleSelectProduct = useCallback(
        (product) => {
            onAddLine?.(ProductRatesModel.fromProduct(product));
        },
        [onAddLine],
    );

    const handleSearch = useCallback(
        (text) => {
            setSearch(text);
            loadProducts(text, -1);
        },
        [loadProducts],
    );

    const handleFolderClick = useCallback(
        (idFolder) => {
            setSearch('');
            loadProducts('', idFolder);
        },
        [loadProducts],
    );

    const renderContent = () => {
        if (result.length === 0) {
            return (
                <div className="rates-add-items-modal__empty">
                    <RatesAddItemsEmpty />
                </div>
            );
        }

        return result.map((section) => (
            <ProductSection key={section.id} onFolderClick={handleFolderClick} section={section}>
                {section.products?.map((product) =>
                    product.isFolder ? (
                        <RatesAddItemRow
                            key={product.key || product.id}
                            hideAction
                            isFolder
                            label={product.name}
                            leading={<Icon name="folder" />}
                            onClick={() => handleFolderClick(product.id)}
                        />
                    ) : (
                        <ProductRow
                            key={product.key || product.id}
                            isAdded={selectedIds.has(String(product.id))}
                            onAdd={() => handleSelectProduct(product)}
                            product={product}
                        />
                    ),
                )}
            </ProductSection>
        ));
    };

    return (
        <RatesAddItemsModal
            error={error}
            isOpen={open}
            loading={loading}
            onClose={handleClose}
            onSearch={handleSearch}
            onSearchInputChange={setSearch}
            placeholder={getLiteral('action_searchproduct')}
            searchValue={search}
            title={title}
        >
            {search ? (
                <div className="rates-add-items-modal__info">
                    <Text>
                        {getLiteral('helptext_products_containing')} &quot;{search}&quot;
                    </Text>
                </div>
            ) : null}
            {renderContent()}
        </RatesAddItemsModal>
    );
});

ProductRatesAddModal.displayName = 'ProductRatesAddModal';

export default ProductRatesAddModal;
